import { ArrowRight, LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import NewsArticleModal from "../components/NewsArticleModal";
import { API } from "../data/siteData";

const categories = ["All", "Company Update", "Partnerships", "Technology", "Development"];

async function fetchNewsArticles(category) {
  const newsResponse = await fetch(`${API}/news?category=${encodeURIComponent(category)}`);
  if (!newsResponse.ok) throw new Error();
  return newsResponse.json();
}

function NewsHero() {
  return (
    <section className="simple-hero simple-hero--short">
      <span className="eyebrow">News & Updates</span>
      <h1>
        THE GAMEDAY
        <br />
        <em>JOURNAL.</em>
      </h1>
      <p>
        Company announcements, founder updates, development progress, partnership news,
        research perspectives, and industry conversations.
      </p>
    </section>
  );
}

function NewsFilters({ category, onChange }) {
  return (
    <div className="filter-row" data-testid="news-category-filters">
      {categories.map((item) => (
        <button
          className={category === item ? "active" : ""}
          data-testid={`news-filter-${item.toLowerCase().replaceAll(" ", "-")}`}
          key={item}
          onClick={() => onChange(item)}
          type="button"
        >
          {item}
        </button>
      ))}
    </div>
  );
}

function NewsResults({ articles, onRead, state }) {
  if (state === "loading") {
    return (
      <div className="loading-state" data-testid="news-loading-state">
        <LoaderCircle className="spin" /> Loading updates
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className="empty-state" data-testid="news-error-state">
        Updates are temporarily unavailable.
      </div>
    );
  }
  return (
    <div className="news-grid" data-testid="news-article-grid">
      {articles.map((article) => (
        <article key={article.article_id} data-testid={`news-article-${article.article_id}`}>
          <img src={article.image_url} alt="" />
          <div>
            <span>{article.category} <i>•</i> {article.date}</span>
            <h2>{article.title}</h2>
            <p>{article.summary}</p>
            <button
              className="text-button"
              data-testid={`read-article-${article.article_id}`}
              onClick={() => onRead(article)}
              type="button"
            >
              Read update <ArrowRight size={16} />
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function NewsletterSignup() {
  return (
    <section className="newsletter-band">
      <div>
        <span className="eyebrow">Stay in the conversation</span>
        <h2>
          RECEIVE GAMEDAY
          <br />
          <em>DEVELOPMENT UPDATES.</em>
        </h2>
      </div>
      <form data-testid="newsletter-form" onSubmit={(event) => event.preventDefault()}>
        <label>
          Email
          <input
            data-testid="newsletter-email-input"
            placeholder="you@organization.com"
            required
            type="email"
          />
        </label>
        <button
          className="button button--red"
          data-testid="newsletter-submit-button"
          type="submit"
        >
          Subscribe
        </button>
      </form>
    </section>
  );
}

export default function NewsPage() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [state, setState] = useState("loading");
  const loadArticles = useCallback(async () => {
    setState("loading");
    try {
      setArticles(await fetchNewsArticles(category));
      setState("ready");
    } catch {
      setState("error");
    }
  }, [category]);
  useEffect(() => {
    void loadArticles();
  }, [loadArticles]);
  return (
    <main data-testid="news-page">
      <NewsHero />
      <section className="content-section news-section">
        <NewsFilters
          category={category}
          onChange={(nextCategory) => {
            setCategory(nextCategory);
            setSelectedArticle(null);
          }}
        />
        <NewsResults
          articles={articles}
          onRead={setSelectedArticle}
          state={state}
        />
      </section>
      <NewsletterSignup />
      <NewsArticleModal
        article={selectedArticle}
        onClose={() => setSelectedArticle(null)}
      />
    </main>
  );
}